// =============================================================================
// AX-Agent API 클라이언트 (BE src/agent/mes-agent.service.ts 응답 미러)
// =============================================================================
import { getApiClient, useAuthStore } from '@axsolutionsdev/core-fe';

export type AgentCheckStatus = 'pass' | 'warn' | 'fail';
export interface AgentCheck {
  label: string;
  status: AgentCheckStatus;
  detail: string;
}
export interface AgentStep {
  key: string;
  label: string;
  status: 'done' | 'warn' | 'error' | 'skipped';
  elapsedMs: number;
  summary: string;
  details?: string[];
  checks?: AgentCheck[];
  sql?: string;
  tables?: { name: string; description: string; columns: string[] }[];
}
export interface AgentColumn {
  key: string;
  label: string;
  type: 'number' | 'string';
}
export interface AgentVizSeries {
  key: string;
  type?: 'bar' | 'line';
  axis?: 0 | 1;
  stack?: string;
}
export interface AgentViz {
  type: 'bar' | 'hbar' | 'line' | 'pie' | 'combo' | 'gantt';
  x: string;
  series: AgentVizSeries[];
  markLine?: { value: number; label: string };
  yLabel?: string;
  /** gantt: 막대 라벨 컬럼 */
  label?: string;
  /** gantt: 가로축 눈금 */
  ticks?: { value: number; label: string }[];
}
export interface AgentInsight {
  model: string;
  version: string;
  operation: string;
  operationLabel: string;
  runId: string;
  inputRows: number;
  evidence: string[];
  elapsedMs: number;
}
export type AgentRow = Record<string, string | number | null>;
export interface AgentAnswer {
  id: string;
  question: string;
  status: 'ok' | 'empty' | 'unsupported' | 'error';
  intent: { key: string; title: string } | null;
  answer: string;
  evidence: string[];
  steps: AgentStep[];
  sql: string | null;
  columns: AgentColumn[];
  rows: AgentRow[];
  /** 모델이 결과 표를 만든 경우 원본 조회 행 수 */
  inputRows: number;
  /** 차트 전용 데이터(없으면 rows) */
  chartRows: AgentRow[] | null;
  viz: AgentViz | null;
  insight: AgentInsight | null;
  warnings: { level: 'error' | 'warning' | 'info'; message: string }[];
  confidence: { score: number; level: 'high' | 'medium' | 'low'; reasons: string[] };
  meta: {
    asOf: string;
    rowCount: number;
    elapsedMs: number;
    period: { from: string; to: string; label: string } | null;
    source: string;
    readOnly: true;
  };
  followUps: string[];
  /** 언어모델 사용 여부 */
  llm: { model: string; used: boolean } | null;
  /** 분석 알고리즘 선택 결과 */
  algorithm: AgentAlgorithm | null;
}
export interface AgentAlgorithmCandidate {
  key: string;
  name: string;
  score: number | null;
  note: string;
  selected: boolean;
}
export interface AgentAlgorithm {
  model: string;
  selected: { key: string; name: string };
  mode: 'auto' | 'manual';
  reason: string;
  metric: string;
  candidates: AgentAlgorithmCandidate[];
}
export interface AgentModel {
  name: string;
  version: string | null;
  description: string;
  operations: string[];
  algorithms: { key: string; name: string; description: string }[];
  status: 'connected' | 'planned';
}
export interface AgentSuggestionGroup {
  model: string;
  label: string;
  questions: string[];
}
export interface AgentMeta {
  connection: {
    system: string;
    database: string;
    engine: string;
    tenant: string;
    tenantCode: string;
    mode: string;
    tables: number;
    sensors: number;
  };
  suggestions: string[];
  suggestionGroups: AgentSuggestionGroup[];
  models: AgentModel[];
  llm: { provider: string; model: string } | null;
}

export async function fetchAgentMeta(): Promise<AgentMeta> {
  const res = await getApiClient().get<AgentMeta>('/mes/agent/meta');
  return res.data;
}

export async function askAgent(
  question: string,
  algorithms: Record<string, string> = {},
): Promise<AgentAnswer> {
  const res = await getApiClient().post<AgentAnswer>('/mes/agent/ask', { question, algorithms });
  return res.data;
}

export interface AgentHistoryItem {
  question: string;
  intent?: string | null;
  period?: string | null;
}

export type AgentStreamEvent =
  | { type: 'step_start'; key: string; label: string }
  | { type: 'step'; step: AgentStep }
  | { type: 'answer_delta'; text: string }
  | { type: 'answer_reset'; text: string }
  | { type: 'result'; answer: AgentAnswer }
  | { type: 'error'; message: string };

/**
 * 처리 단계·답변 문장을 SSE 로 받는다. 401 이면 axios 경유 호출로 토큰을 갱신한 뒤 1회 재시도.
 */
export async function askAgentStream(
  question: string,
  history: AgentHistoryItem[],
  onEvent: (ev: AgentStreamEvent) => void,
  algorithms: Record<string, string> = {},
): Promise<AgentAnswer> {
  const client = getApiClient();
  const base = (client.defaults.baseURL ?? '/api').replace(/\/$/, '');
  const open = (): Promise<Response> => {
    const token = useAuthStore().accessToken;
    return fetch(`${base}/mes/agent/ask/stream`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ question, history, algorithms }),
    });
  };
  let res = await open();
  if (res.status === 401) {
    await client.get('/mes/agent/meta');
    res = await open();
  }
  if (!res.ok || !res.body) throw new Error(`Agent 스트림 연결 실패 (HTTP ${res.status})`);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = '';
  let result: AgentAnswer | null = null;
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buf.indexOf('\n\n')) >= 0) {
      const chunk = buf.slice(0, idx);
      buf = buf.slice(idx + 2);
      const line = chunk.split('\n').find((l) => l.startsWith('data:'));
      if (!line) continue;
      const ev = JSON.parse(line.slice(5).trim()) as AgentStreamEvent;
      if (ev.type === 'result') result = ev.answer;
      if (ev.type === 'error') throw new Error(ev.message);
      onEvent(ev);
    }
  }
  if (!result) throw new Error('Agent 응답이 중간에 끊겼습니다.');
  return result;
}
